import { cloneState, numberField, type SyncrelEntity, type SyncrelState } from "./types";

const legacyFieldAliases: Record<string, string> = {
  saudiEnterprise: "enterpriseAccount",
};

const legacyFieldKeys = new Set(Object.keys(legacyFieldAliases));

const riceStarterDefaults: Record<string, Record<string, number>> = {
  baseline: {
    reach: 1800,
    impact: 1,
    confidence: 70,
    effort: 4,
  },
  lever: {
    reach: 5200,
    impact: 2,
    confidence: 75,
    effort: 12,
  },
  bet: {
    reach: 2400,
    impact: 3,
    confidence: 60,
    effort: 24,
  },
};

export const migrateLegacyFieldReferences = (state: SyncrelState): SyncrelState => {
  const hasLegacySchemaField = state.schema.fields.some((field) => legacyFieldKeys.has(field.key));
  const hasLegacyEntityField = state.entities.some((entity) => (
    Object.keys(entity.fields).some((key) => legacyFieldKeys.has(key))
  ));
  const hasCollapsedRiceStarter = state.entities.some(needsRiceStarterRepair);
  if (!hasLegacySchemaField && !hasLegacyEntityField && !hasCollapsedRiceStarter) return state;

  const next = cloneState(state);
  if (hasLegacySchemaField || hasLegacyEntityField) {
    next.schema.fields = next.schema.fields.filter((field) => !legacyFieldKeys.has(field.key));
    next.entities = next.entities.map((entity) => {
      const fields = { ...entity.fields };
      for (const [legacyKey, nextKey] of Object.entries(legacyFieldAliases)) {
        if (legacyKey in fields) {
          if (!(nextKey in fields)) fields[nextKey] = fields[legacyKey];
          delete fields[legacyKey];
        }
      }
      return { ...entity, fields };
    });
  }
  return hasCollapsedRiceStarter ? repairCollapsedRiceStarter(next) : next;
};

const needsRiceStarterRepair = (entity: SyncrelEntity): boolean => {
  const role = riceStarterRole(entity);
  if (!role) return false;
  return (
    numberField(entity.fields, "reach") === 0 &&
    numberField(entity.fields, "valueScore") === 0 &&
    numberField(entity.fields, "riceScore") === 0 &&
    numberField(entity.fields, "effort", 1) === 1
  );
};

const riceStarterRole = (entity: SyncrelEntity): string | null => {
  if (entity.fields.frameworkCanvas !== "rice") return null;
  if (entity.fields.frameworkStarterFramework !== "rice") return null;
  const role = entity.fields.frameworkStarterRole;
  if (typeof role !== "string") return null;
  return role in riceStarterDefaults ? role : null;
};

const repairCollapsedRiceStarter = (state: SyncrelState): SyncrelState => {
  const withStarterFields = state.entities.map((entity) => {
    if (!needsRiceStarterRepair(entity)) return entity;
    const role = riceStarterRole(entity);
    if (!role) return entity;
    return {
      ...entity,
      fields: {
        ...entity.fields,
        ...riceStarterDefaults[role],
      },
    };
  });
  const scopedEntities = withStarterFields.filter((entity) => (
    entity.fields.frameworkCanvas === "rice" &&
    (entity.type === "feature" || entity.type === "deliverable")
  ));
  const maxValue = Math.max(
    1,
    ...scopedEntities.map((entity) => {
      const reach = numberField(entity.fields, "reach");
      const impact = numberField(entity.fields, "impact", 1);
      const confidence = numberField(entity.fields, "confidence", 100) / 100;
      return reach * impact * confidence;
    }),
  );

  return {
    ...state,
    entities: withStarterFields.map((entity) => {
      const role = riceStarterRole(entity);
      if (!role || (entity.type !== "feature" && entity.type !== "deliverable")) return entity;
      const reach = numberField(entity.fields, "reach");
      const impact = numberField(entity.fields, "impact", 1);
      const confidence = numberField(entity.fields, "confidence", 100) / 100;
      const effort = Math.max(1, numberField(entity.fields, "effort", 1));
      const valueScore = Math.round(reach * impact * confidence);
      const riceScore = Math.round((valueScore / effort) * 100) / 100;
      const effortRatio = Math.min(1, effort / 40);
      const valueRatio = Math.min(1, valueScore / maxValue);

      return {
        ...entity,
        fields: {
          ...entity.fields,
          reach,
          impact,
          confidence: Math.round(confidence * 100),
          effort,
          valueScore,
          riceScore,
        },
        position: {
          x: 180 + valueRatio * 560,
          y: 560 - effortRatio * 340,
        },
      };
    }),
  };
};
